// ガイドブックの中身。章とページ、そして「どこを説明するか」の指定。
//
// capture はキャプチャのファイル名に使う（builder_guide/{book}_{capture}_{locale}.webp）。
// focus はキャプチャに対する比率（0〜1）。実画面を getBoundingClientRect で実測した値なので、
// 目分量ではなくボタンの位置にぴったり合う。UI を動かしたら測り直すこと。
// キャプチャの撮り直しは scripts/capture-guide.mjs。

import {
  GuideRect,
  GuideBookPage,
  GuideFocusMode,
  GuideBookChapter,
} from "features/builder/elements/BuilderGuideBook";

const KEY = "MSG_BUILDER_GUIDEBOOK";

const full = (capture: string, key: string): GuideBookPage => ({
  key: `${capture}_${key}`,
  capture,
  mode: GuideFocusMode.FULL,
  titleKey: `${KEY}_${key.toUpperCase()}_TITLE`,
  descKey: `${KEY}_${key.toUpperCase()}_DESC`,
});

const spot = (
  capture: string,
  key: string,
  mode: GuideFocusMode,
  focus: GuideRect,
): GuideBookPage => ({
  key: `${capture}_${key}`,
  capture,
  mode,
  focus,
  titleKey: `${KEY}_${key.toUpperCase()}_TITLE`,
  descKey: `${KEY}_${key.toUpperCase()}_DESC`,
});

const ring = (capture: string, key: string, focus: GuideRect) =>
  spot(capture, key, GuideFocusMode.HIGHLIGHT, focus);

const zoom = (capture: string, key: string, focus: GuideRect) =>
  spot(capture, key, GuideFocusMode.ZOOM, focus);

export const TOP_GUIDE_CHAPTERS: GuideBookChapter[] = [
  {
    key: "mine",
    icon: "folder",
    labelKey: `${KEY}_CH_MINE`,
    pages: [
      full("mine", "mine_overview"),
      zoom("mine", "mine_guide", { x: 0.779, y: 0.025, w: 0.12, h: 0.05 }),
      ring("mine", "mine_card", { x: 0.156, y: 0.167, w: 0.393, h: 0.629 }),
      zoom("mine", "mine_stats", { x: 0.169, y: 0.674, w: 0.104, h: 0.035 }),
      zoom("mine", "mine_actions", { x: 0.161, y: 0.717, w: 0.383, h: 0.071 }),
      zoom("mine", "mine_filter", { x: 0.007, y: 0.235, w: 0.133, h: 0.177 }),
      zoom("mine", "mine_sort", { x: 0.007, y: 0.412, w: 0.133, h: 0.242 }),
      ring("mine", "mine_tabs", { x: 0.964, y: 0.167, w: 0.036, h: 0.481 }),
      zoom("mine", "mine_trash", { x: 0.01, y: 0.942, w: 0.117, h: 0.058 }),
    ],
  },
  {
    key: "public",
    icon: "public",
    labelKey: `${KEY}_CH_PUBLIC`,
    pages: [
      full("public", "public_overview"),
      zoom("public", "public_actions", { x: 0.161, y: 0.717, w: 0.383, h: 0.071 }),
      zoom("public", "public_filter", { x: 0.007, y: 0.235, w: 0.133, h: 0.177 }),
      zoom("public", "public_sort", { x: 0.007, y: 0.412, w: 0.133, h: 0.242 }),
    ],
  },
  {
    key: "detail",
    icon: "info",
    labelKey: `${KEY}_CH_DETAIL`,
    pages: [
      full("detail", "detail_overview"),
      zoom("detail", "detail_tabs", { x: 0.598, y: 0.292, w: 0.392, h: 0.117 }),
      zoom("detail", "detail_info", { x: 0.598, y: 0.408, w: 0.392, h: 0.575 }),
      zoom("detail", "detail_reaction", { x: 0.614, y: 0.463, w: 0.36, h: 0.148 }),
      zoom("detail", "detail_actions", { x: 0.01, y: 0.829, w: 0.572, h: 0.154 }),
      zoom("detail", "detail_report", { x: 0.535, y: 0.208, w: 0.031, h: 0.05 }),
      full("own", "detail_own"),
    ],
  },
  {
    key: "trash",
    icon: "delete",
    labelKey: `${KEY}_CH_TRASH`,
    pages: [
      zoom("own", "trash_send", { x: 0.535, y: 0.208, w: 0.031, h: 0.05 }),
      full("trash", "trash_overview"),
      zoom("trash", "trash_restore", { x: 0.161, y: 0.717, w: 0.383, h: 0.071 }),
    ],
  },
];

export const EDITOR_GUIDE_CHAPTERS: GuideBookChapter[] = [
  {
    key: "screen",
    icon: "dashboard",
    labelKey: `${KEY}_CH_SCREEN`,
    pages: [
      full("main", "screen_overview"),
      ring("main", "screen_header", { x: 0.22, y: 0.008, w: 0.599, h: 0.142 }),
      ring("main", "screen_steps", { x: 0.005, y: 0.158, w: 0.375, h: 0.624 }),
      ring("main", "screen_tools", { x: 0.911, y: 0.167, w: 0.083, h: 0.817 }),
      ring("main", "screen_assets", { x: 0.241, y: 0.888, w: 0.571, h: 0.104 }),
      zoom("main", "screen_back", { x: 0, y: 0, w: 0.1, h: 0.115 }),
    ],
  },
  {
    key: "build",
    icon: "widgets",
    labelKey: `${KEY}_CH_BUILD`,
    pages: [
      zoom("main", "build_blocks", { x: 0.241, y: 0.888, w: 0.427, h: 0.104 }),
      zoom("main", "build_open", { x: 0.668, y: 0.898, w: 0.061, h: 0.083 }),
      zoom("assets", "build_list", { x: 0.214, y: 0.216, w: 0.573, h: 0.569 }),
      zoom("main", "build_tap", { x: 0.921, y: 0.417, w: 0.065, h: 0.208 }),
      zoom("main", "build_undo", { x: 0.921, y: 0.181, w: 0.065, h: 0.208 }),
      zoom("main", "build_zoom", { x: 0.921, y: 0.652, w: 0.065, h: 0.317 }),
      full("history", "build_history"),
    ],
  },
  {
    key: "setting",
    icon: "tune",
    labelKey: `${KEY}_CH_SETTING`,
    pages: [
      ring("main", "setting_steps", { x: 0.005, y: 0.158, w: 0.375, h: 0.624 }),
      zoom("main", "setting_character", { x: 0.016, y: 0.269, w: 0.354, h: 0.162 }),
      full("commands", "setting_commands"),
      zoom("main", "setting_size", { x: 0.233, y: 0.083, w: 0.079, h: 0.05 }),
      full("ai", "setting_ai"),
      full("info", "setting_info"),
    ],
  },
  {
    key: "publish",
    icon: "rocket_launch",
    labelKey: `${KEY}_CH_PUBLISH`,
    pages: [
      zoom("main", "publish_test", { x: 0.606, y: 0.083, w: 0.096, h: 0.05 }),
      full("test", "publish_play"),
      zoom("test", "publish_commands", { x: 0.007, y: 0.158, w: 0.24, h: 0.824 }),
      zoom("test", "publish_start", { x: 0.521, y: 0.881, w: 0.193, h: 0.083 }),
      zoom("main", "publish_step", { x: 0.016, y: 0.539, w: 0.354, h: 0.077 }),
      zoom("main", "publish_publish", { x: 0.005, y: 0.701, w: 0.375, h: 0.081 }),
      zoom("main", "publish_help", { x: 0.71, y: 0.083, w: 0.096, h: 0.05 }),
    ],
  },
];
